import React from "react";

interface ConnectionButtonProps {
  isConnected: boolean;
  isConnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

export const ConnectionButton: React.FC<ConnectionButtonProps> = ({
  isConnected,
  isConnecting,
  onConnect,
  onDisconnect,
}) => {
  return (
    <div className="config-actions">
      <button
        className="nav-button"
        onClick={onConnect}
        disabled={isConnected || isConnecting}
        style={{
          backgroundColor: isConnected
            ? "#ccc"
            : isConnecting
              ? "#ffc107"
              : "#28a745",
          color: "white",
          cursor: isConnected || isConnecting ? "not-allowed" : "pointer",
        }}
      >
        {isConnecting
          ? "⏳ Connecting..."
          : isConnected
            ? "🟢 Connected"
            : "Connect"}
      </button>
      {isConnecting && (
        <span style={{ color: "#666", fontSize: "0.9rem" }}>
          Connection in progress... Check messages below for details
        </span>
      )}
      <button
        className="nav-button"
        onClick={onDisconnect}
        disabled={!isConnected}
        style={{
          backgroundColor: !isConnected ? "#ccc" : "#dc3545",
          cursor: !isConnected ? "not-allowed" : "pointer",
        }}
      >
        Disconnect
      </button>
    </div>
  );
};

interface ClearButtonProps {
  onClick: () => void;
}

export const ClearButton: React.FC<ClearButtonProps> = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "0.5rem 1rem",
        fontSize: "0.9rem",
        backgroundColor: "#6c757d",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      Clear
    </button>
  );
};

interface SubscriptionButtonProps {
  channel: string;
  isSubscribed: boolean;
  isConnected: boolean;
  disabled?: boolean;
  /** When true, show subscribing state and disable (e.g. waiting for multiplex ack) */
  isSubscribing?: boolean;
  /** When true, show unsubscribing state and disable (prevents rapid unsubscribe clicks) */
  isUnsubscribing?: boolean;
  subscribeLabel: string;
  unsubscribeLabel: string;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
  variant?: "public" | "private";
}

export const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({
  channel,
  isSubscribed,
  isConnected,
  disabled = false,
  isSubscribing = false,
  isUnsubscribing = false,
  subscribeLabel,
  unsubscribeLabel,
  onSubscribe,
  onUnsubscribe,
  variant = "public",
}) => {
  const isDisabled = !isConnected || disabled || isSubscribing || isUnsubscribing;
  const backgroundColor = isSubscribed
    ? isUnsubscribing
      ? "#ffc107"
      : "#dc3545"
    : isSubscribing
      ? "#ffc107"
      : variant === "private"
        ? "#28a745"
        : "#007bff";

  const label = isSubscribed
    ? isUnsubscribing
      ? "Unsubscribing…"
      : unsubscribeLabel
    : isSubscribing
      ? "Subscribing…"
      : subscribeLabel;

  return (
    <button
      onClick={isSubscribed ? onUnsubscribe : onSubscribe}
      disabled={isDisabled}
      style={{
        padding: "0.5rem 1rem",
        fontSize: "0.9rem",
        backgroundColor,
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
    >
      {label}
    </button>
  );
};
